import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import './DialogStyles.css';

interface Toast {
  id: string;
  message: string;
  type: 'success' | 'error' | 'info';
}

interface ModalConfig {
  type: 'alert' | 'confirm';
  message: string;
  title?: string;
  resolve: (value: boolean | PromiseLike<boolean>) => void;
}

interface DialogContextType {
  toast: (message: string, type?: 'success' | 'error' | 'info') => void;
  alert: (message: string, title?: string) => Promise<void>;
  confirm: (message: string, title?: string) => Promise<boolean>;
}

const DialogContext = createContext<DialogContextType | null>(null);

export const useDialog = () => {
  const context = useContext(DialogContext);
  if (!context) {
    throw new Error('useDialog must be used within a DialogProvider');
  }
  return context;
};

export const DialogProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const [modal, setModal] = useState<ModalConfig | null>(null);
  
  const confirmButtonRef = useRef<HTMLButtonElement>(null);

  // Focus confirm button when modal opens
  useEffect(() => {
    if (modal) {
      // Small timeout to allow render
      const timer = setTimeout(() => {
        confirmButtonRef.current?.focus();
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [modal]);

  // Handle Toast dismiss
  const dismissToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  // Show Toast
  const toast = useCallback((message: string, type: 'success' | 'error' | 'info' = 'success') => {
    const id = crypto.randomUUID();
    setToasts((prev) => [...prev, { id, message, type }]);

    // Auto dismiss
    setTimeout(() => {
      dismissToast(id);
    }, 3000);
  }, [dismissToast]);

  // Show Alert
  const alert = useCallback((message: string, title: string = 'Notice'): Promise<void> => {
    return new Promise<boolean>((resolve) => {
      setModal({
        type: 'alert',
        message,
        title,
        resolve: resolve as any,
      });
    }).then(() => {});
  }, []);

  // Show Confirm
  const confirm = useCallback((message: string, title: string = 'Confirm Action'): Promise<boolean> => {
    return new Promise<boolean>((resolve) => {
      setModal({
        type: 'confirm',
        message,
        title,
        resolve,
      });
    });
  }, []);

  // Modal actions
  const handleConfirm = useCallback(() => {
    if (modal) {
      modal.resolve(true);
      setModal(null);
    }
  }, [modal]);

  const handleCancel = useCallback(() => {
    if (modal) {
      modal.resolve(false);
      setModal(null);
    }
  }, [modal]);

  // Keyboard events when modal is open
  useEffect(() => {
    if (!modal) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        handleCancel();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [modal, handleCancel]);

  return (
    <DialogContext.Provider value={{ toast, alert, confirm }}>
      {children}

      {/* Floating Toast Containers */}
      {toasts.length > 0 && (
        <div className="toast-container">
          {toasts.map((t) => (
            <div key={t.id} className={`custom-toast toast-${t.type}`}>
              <span className="toast-icon">
                {t.type === 'success' && '✅'}
                {t.type === 'error' && '❌'}
                {t.type === 'info' && 'ℹ️'}
              </span>
              <div className="toast-message">{t.message}</div>
              <button className="toast-close" onClick={() => dismissToast(t.id)}>
                &times;
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Custom Modal Dialog Backdrop & Card */}
      {modal && (
        <div className="custom-modal-overlay" onClick={handleCancel}>
          <div className="custom-modal-card" onClick={(e) => e.stopPropagation()}>
            <div className="custom-modal-header">
              <h3>
                <span>
                  {modal.type === 'confirm' ? '❓' : 'ℹ️'}
                </span>
                {modal.title}
              </h3>
            </div>
            
            <div className="custom-modal-body">
              {modal.message}
            </div>

            <div className="custom-modal-footer">
              {modal.type === 'confirm' && (
                <button 
                  className="dialog-btn dialog-btn-cancel" 
                  onClick={handleCancel}
                >
                  Cancel
                </button>
              )}
              <button 
                ref={confirmButtonRef}
                className="dialog-btn dialog-btn-confirm" 
                onClick={handleConfirm}
              >
                {modal.type === 'confirm' ? 'Confirm' : 'OK'}
              </button>
            </div>
          </div>
        </div>
      )}
    </DialogContext.Provider>
  );
};
